import { useState, useEffect } from "react"
import * as MdIcons from 'react-icons/md'
import { useEndpointProvider } from "../../../../../Providers/EndpointProvider";
import ModalHeader from "../../../../../Components/ModalHeader/ModalHeader";
import { ModalFooter } from "../../../../../Constants/StyledComponents";
import ModalScrollbar from "../../../../../Components/ModalScrollbar/ModalScrollbar"
import { UploadStepHeader } from "../../../../../Constants/StyledComponents";
import CircularCheckboxComponent from "../../../../../Components/CircularCheckbox/CircularCheckbox";
import { useUploadProcessProvider } from "../../../../../Providers/UploadProcessProvider";
import { UploadFilesModal, InfoButtonLabel, ModalTextInput, SelectExistingPatientDiv, PatientIDValidationMessage, HiddenButton } from "../../../../../Constants/StyledComponents";
import { useSitesProvider } from "../../../../../Providers/SitesProvider";
import ModernMUITable from "../../../../../Components/ModernMUITable"
import { MobergButton } from "../../../../../Components/MobergButton/MobergButton";
import { MobergTheme } from "../../../../../Components/MobergThemes/MobergColors";
import { MobergButtonVariant } from "../../../../../Components/MobergButton/MobergButton";
import { MobergButtonShape } from "../../../../../Components/MobergButton/MobergButton";
import { formatDuration } from "../../../../../Computation/utilFunctions";
import { useBackendLinksProvider } from "../../../../../Providers/BackendLinksProvider";
import PatientIDType, { DEFAULT_PATIENT_ID_LENGTH, DEFAULT_PATIENT_ID_TYPE } from "./PatientIDType"
import { MobergFontFamily } from "../../../../../Components/MobergFont/MobergFont"
import { useQueryClient } from "@tanstack/react-query"

const patientColumns = [
	{ field: "patient_id", flex: 0.4, headerName: "Patient ID", minWidth: 300, visible: true },
	{
		field: "cns_duration",
		flex: 1,
		headerName: "Duration",
		visible: true,
		valueGetter: (value, row) => formatDuration(row.cns_duration),
	}
]

const UploadPatientSite = ({ setToPrevWindow, setToNextWindow }) => {
	const endpointProvider = useEndpointProvider()
	const sitesProvider = useSitesProvider()
	const uploadProcessProvider = useUploadProcessProvider()
	const { LINKS } = useBackendLinksProvider()

	function handlePatientIDTypeValidation(event) {
		const type = uploadProcessProvider.patientIDType;
		const regexMap = {
			[PatientIDType.NUMERIC]: /^[0-9]$/,
			[PatientIDType.ALPHA]: /^[a-zA-Z]$/,
			[PatientIDType.ALPHANUMERIC]: /^[a-zA-Z0-9]$/,
			[PatientIDType.ALPHA_LOWER]: /^[a-z]$/,
			[PatientIDType.ALPHA_UPPER]: /^[A-Z]$/,
			[PatientIDType.ALPHANUMERIC_UPPER]: /^[A-Z0-9]$/,
			[PatientIDType.ALPHANUMERIC_LOWER]: /^[a-z0-9]$/,
		};

		const regex = regexMap[type] || regexMap[DEFAULT_PATIENT_ID_TYPE]  // Use NUMERIC as default if type is not defined

		if (!regex.test(event.key)) {
			event.preventDefault()
		}
	}

	const queryClient = useQueryClient()
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })
	const [sortModel, setSortModel] = useState([{ field: 'patient_id', sort: 'asc' }])

	// Look up patient data from React Query's cache (written by ModernMUITable's internal fetch)
	// This avoids a duplicate fetch that could desync from the table's actual displayed data.
	const getPatientsById = () => {
		const queries = queryClient.getQueriesData({ queryKey: ["project_patients"] })
		const allPatients = {}
		for (const [, data] of queries) {
			if (data?.data) {
				for (const patient of data.data) {
					allPatients[patient.id] = patient
				}
			}
		}
		return allPatients
	}

	const handleBack = () => {
		setToPrevWindow(true)
	}

	const handleNext = () => {
		setToNextWindow(true)
	}

	/**
	 * This function is called when the user selects a patientID from the table
	 * If the user selects a patientID that is different from the previously selected site, the user is prompted to confirm but only when they have select a file in the later steps
	 */
	function onCheckPatient(patient, isClicked) {
		uploadProcessProvider.resetExistingTableData()
		if (uploadProcessProvider.patientID !== patient) {
			if (!uploadProcessProvider.checkFoldersIsEmpty()) {
				if (window.confirm("Modifying existing site ID will remove your previously selected upload files. Do you want to continue?")) {
					uploadProcessProvider.resetTableData()
					uploadProcessProvider.resetUploadFolders()
					uploadProcessProvider.resetDirectoryNames()
				} else return
			}
		}
		uploadProcessProvider.setPatientID(isClicked ? patient.patient_id : "")
		uploadProcessProvider.setPatientPrimaryKey(isClicked ? patient.id : "")
	}

	/**
	 * The following useStates handle validation for inputting a new Patient ID
	 * patientIDAvailable is true when the patient ID is available
	 */
	const [patientIDAvailable, setPatientIDAvailable] = useState(false)
	const [patientIDExists, setPatientIDExists] = useState(false)
	const [patientIDError, setPatientIDError] = useState(false)
	const [checkingPatientID, setCheckingPatientID] = useState(false)
	const [validationMessageType, setValidationMessageType] = useState("")
	const [validationMessageContent, setValidationMessageContent] = useState("")

	// This is used to track the primary key of an existing patient so preselect it in the table
	const [existingPatientKey, setExistingPatientKey] = useState(undefined)

	async function checkPatientIdentifier(body) {
		return endpointProvider.post(LINKS.DATA.UPLOAD.CHECK_PATIENT_IDENTIFIER, body)
	}

	/**
	 * This function checks if the new patient ID is valid
	 */
	useEffect(() => {
		if (!uploadProcessProvider.patientID && !patientIDError && !patientIDAvailable && !patientIDExists) {
			return
		}
		if (String(uploadProcessProvider.patientID).length === 0 || uploadProcessProvider.patientID === null || uploadProcessProvider.patientID === "") {
			setValidationMessageContent("This field is required.")
			setPatientIDExists(false)
			setExistingPatientKey(undefined)
			setPatientIDAvailable(false)
			setCheckingPatientID(false)
			setPatientIDError(true)
			return
		} else if (String(uploadProcessProvider.patientID).length < (uploadProcessProvider.patientIDMinLength || uploadProcessProvider.patientIDLength || DEFAULT_PATIENT_ID_LENGTH)
			|| String(uploadProcessProvider.patientID).length > (uploadProcessProvider.patientIDLength || DEFAULT_PATIENT_ID_LENGTH)) {
			const maxLength = uploadProcessProvider.patientIDLength || DEFAULT_PATIENT_ID_LENGTH
			const minLength = uploadProcessProvider.patientIDMinLength || maxLength
			setValidationMessageType("Error! ")
			setValidationMessageContent(minLength === maxLength
				? `Patient ID must be ${maxLength} ${maxLength === 1 ? 'character' : 'characters'}.`
				: `Patient ID must be ${minLength}-${maxLength} characters.`)
			setPatientIDExists(false)
			setExistingPatientKey(undefined)
			setPatientIDAvailable(false)
			setCheckingPatientID(false)
			setPatientIDError(true)
			return
		} else {
			setValidationMessageType("Warning! ")
			setValidationMessageContent("Checking Patient ID...")
			setPatientIDError(false)
			setPatientIDAvailable(false)
			setPatientIDExists(false)
			setExistingPatientKey(undefined)
			setCheckingPatientID(true)
		}
		checkPatientIdentifier({ site_id: uploadProcessProvider.selectedSitePrimaryKey, project_id: uploadProcessProvider.selectedProjectPrimaryKey, patient_identifier: uploadProcessProvider.patientID })
			.then(data => {
				if (data["exist"]) {
					setValidationMessageType("Warning! ")
					setValidationMessageContent(`This Patient ID already exists in Site ${uploadProcessProvider.selectedSiteID}.`)
					setPatientIDError(false)
					setPatientIDAvailable(false)
					setCheckingPatientID(false)
					setPatientIDExists(true)
					// If the patient exists, then set their primary key
					setExistingPatientKey(data['patient_key'])
					return
				} else {
					setValidationMessageType("")
					setValidationMessageContent("This Patient ID is available.")
					setPatientIDError(false)
					setPatientIDExists(false)
					setExistingPatientKey(undefined)
					setCheckingPatientID(false)
					setPatientIDAvailable(true)
					return
				}
			})
			.catch(e => {
				alert(e)
				return false
			})
	}, [uploadProcessProvider.patientID])

	/**
	 * The following functions handle the circular checkboxes for selecting existing patients or uploading a new patient
	 * It is going to uncheck an already checked checkbox and check the one that was clicked
	 * @returns
	 */
	function handleExistingPatientsCheckbox() {
		if (!uploadProcessProvider.checkFoldersIsEmpty()) {
			if (window.confirm("Modifying existing site ID will remove your previously selected upload files. Do you want to continue?")) {
				uploadProcessProvider.resetTableData()
				uploadProcessProvider.resetUploadFolders()
				uploadProcessProvider.resetDirectoryNames()
			} else return
		}
		uploadProcessProvider.setExistingPatientsCheckedProvider(true)
		if (uploadProcessProvider.newPatientCheckedProvider) {
			uploadProcessProvider.setNewPatientCheckedProvider(!uploadProcessProvider.newPatientCheckedProvider)
		} else {
			uploadProcessProvider.setNewPatientCheckedProvider(uploadProcessProvider.newPatientCheckedProvider)
		}
		uploadProcessProvider.setPatientID("")
	}

	function handleNewPatientCheckbox() {
		if (!uploadProcessProvider.checkFoldersIsEmpty()) {
			if (window.confirm("Modifying existing site ID will remove your previously selected upload files. Do you want to continue?")) {
				uploadProcessProvider.resetTableData()
				uploadProcessProvider.resetUploadFolders()
				uploadProcessProvider.resetDirectoryNames()
			} else return
		}
		uploadProcessProvider.resetTableData()
		uploadProcessProvider.resetUploadFolders()
		uploadProcessProvider.resetDirectoryNames()
		uploadProcessProvider.setNewPatientCheckedProvider(true)
		if (uploadProcessProvider.existingPatientsCheckedProvider) {
			uploadProcessProvider.setExistingPatientsCheckedProvider(!uploadProcessProvider.existingPatientsCheckedProvider)
		} else {
			uploadProcessProvider.setExistingPatientsCheckedProvider(uploadProcessProvider.existingPatientsCheckedProvider)
		}
		uploadProcessProvider.setPatientID("")
	}

	/**
	 * This function return user to the existing Patient ID that is duplicate with the new Patient ID that they created
	 */
	function handleNavigationToExistingPatient() {
		uploadProcessProvider.setNewPatientCheckedProvider(!uploadProcessProvider.newPatientCheckedProvider)
		uploadProcessProvider.setExistingPatientsCheckedProvider(!uploadProcessProvider.existingPatientsCheckedProvider)

		// Previous implementations relied on iterating through the table via tableRef, but this was flawed as the new MUITable only loads in a page in a time. This means that if you wanted to select an existing patient that was on a different page it couldn't be found. This existingPatientKey is set from the check_patient_identifier endpoint when the patient exists.
		setSelectedRows([existingPatientKey])
		uploadProcessProvider?.setPatientPrimaryKey(existingPatientKey)
	}

	const [selectedRows, setSelectedRows] = useState(uploadProcessProvider?.patientPrimaryKey === '' ? [] : [uploadProcessProvider?.patientPrimaryKey])

	// Handle patient selection change - look up patient from React Query cache
	const handlePatientSelectionChange = (selectionArray) => {
		setSelectedRows(selectionArray);

		if (selectionArray.length === 0) {
			// Clear selection
			uploadProcessProvider.setPatientID("");
			uploadProcessProvider.setPatientPrimaryKey("");
		} else {
			const selectedId = selectionArray[0];
			const patientsById = getPatientsById();
			const selectedPatient = patientsById[selectedId];

			if (selectedPatient) {
				onCheckPatient(selectedPatient, true);
			}
		}
	}

	// Modal table toolbar props - minimal for upload workflow
	const uploadTableToolbarProps = {
		searchInput: { isShow: true },
		columnButton: { isShow: false }
	}

	return (
		<>
			<UploadFilesModal>
				<ModalHeader headerText="Specify Patient" resetCache={uploadProcessProvider.resetCache} />
				<ModalScrollbar>
					<div>
						<UploadStepHeader>3. Select an existing patient or create a new patient</UploadStepHeader>
					</div>

					<div style={{ marginLeft: "auto", marginRight: "auto", textAlign: "center" }}>
						<p style={{fontWeight: "600", marginBottom: "5px"}}>Site: {uploadProcessProvider.selectedSiteName}
						</p>
					</div>

					<div style={{ marginLeft: "auto", marginRight: "auto", textAlign: "center" }}>
						<p style={{fontWeight: "600", marginBottom: "5px" }}>Project: {uploadProcessProvider.selectedProjectName}</p>
					</div>

					<div
						id="circularCheckboxContent"
						style={{ marginRight: "auto", display: "flex", marginTop: "10px", marginLeft: "auto", textAlign: "center", justifyContent: "center" }}
					>
						<CircularCheckboxComponent
							style={{ color: uploadProcessProvider.existingPatientsCheckedProvider ? "var(--color-accent-interactive)" : "var(--text-secondary-color)", marginRight: "20px" }}
							checked={uploadProcessProvider.existingPatientsCheckedProvider}
							onChangeFxn={handleExistingPatientsCheckbox}
							checkboxLabel={"Existing patient"}
						/>
						<CircularCheckboxComponent
							style={{ color: uploadProcessProvider.newPatientCheckedProvider ? "var(--color-accent-interactive)" : "var(--text-secondary-color)" }}
							checked={uploadProcessProvider.newPatientCheckedProvider}
							onChangeFxn={handleNewPatientCheckbox}
							checkboxLabel={"New patient"}
						/>
					</div>

					<div
						id="existingPatientsContentDiv"
						style={{
							marginTop: "25px",
							display: uploadProcessProvider.existingPatientsCheckedProvider ? "flex" : "none",
							flexDirection: 'column',
							width: "100%",
							marginLeft: "auto",
							marginRight: "auto",
							maxHeight: '450px',
							overflow: 'auto'
						}}
					>
						<ModernMUITable 
							columns={patientColumns} 
							queryKey="project_patients"
							endpoint={LINKS.DATA.UPLOAD.GET_PROJECT_PATIENTS_BY_SITE}
							body={{ 
								project_id: uploadProcessProvider.selectedProjectPrimaryKey, 
								site_id: uploadProcessProvider.selectedSitePrimaryKey 
							}}
							optionalParams={{}}
							paginationModel={paginationModel}
							onPaginationModelChange={setPaginationModel}
							sortModel={sortModel}
							onSortModelChange={setSortModel}
							paginationMode="server"
							sortingMode="server"
							enableRowSelection={true}
							checkboxSelection={true}
							supportsPageSize={true}
							onSelectionChange={handlePatientSelectionChange}
							rowSelectionModel={{ type: 'include', ids: new Set(selectedRows) }}
							isRowSelectable={() => true}
							disableRowSelectionOnClick={false}
							disableSelectAll={true}
							showToolbar={true}
							height="80%"
							style={{ padding: '10px' }}
						/>
					</div>

					<div
						id="newPatientContentDiv"
						style={{
							marginTop: "25px",
							display: uploadProcessProvider.newPatientCheckedProvider ? "block" : "none",
							width: "700px",
							marginLeft: "auto",
							marginRight: "auto",
							textAlign: "center",
						}}
					>
						<div style={{ position: "relative", width: "415px", marginLeft: "auto", marginRight: "auto", textAlign: "center" }}>
							<ModalTextInput
								placeholder="Enter a new Patient ID"
								onKeyPress={event => handlePatientIDTypeValidation(event)}
								maxLength={uploadProcessProvider.patientIDLength || DEFAULT_PATIENT_ID_LENGTH}
								onChange={e => {
									uploadProcessProvider.setPatientID(e.target.value)
								}}
								style={{
									border: patientIDAvailable
										? "2px solid var(--color-border-default)"
										: patientIDExists || checkingPatientID
											? "2px solid #EEA10C"
											: patientIDError
												? "2px solid #E54E58"
												: "2px solid var(--color-border-default)",
								}}
								value={uploadProcessProvider.patientID}
							/>

							{patientIDAvailable ? (
								<MdIcons.MdOutlineCheckCircleOutline
									size={30}
									style={{ position: "absolute", right: "15px", top: "12px", color: "#0FA95F", display: patientIDAvailable ? "block" : "none" }}
								/>
							) : patientIDExists || checkingPatientID ? (
								<MdIcons.MdOutlineErrorOutline
									size={30}
									style={{
										position: "absolute",
										right: "15px",
										top: "12px",
										color: "#EEA10C",
										display: patientIDExists || checkingPatientID ? "block" : "none",
									}}
								/>
							) : patientIDError ? (
								<MdIcons.MdOutlineErrorOutline
									size={30}
									style={{ position: "absolute", right: "15px", top: "12px", color: "#E54E58", display: patientIDError ? "block" : "none" }}
								/>
							) : null}
						</div>

						<PatientIDValidationMessage
							style={{
								display: patientIDError ? "block" : patientIDExists || checkingPatientID ? "block" : patientIDAvailable ? "block" : "none",
								color: patientIDAvailable ? "#0FA95F" : patientIDExists || checkingPatientID ? "#EEA10C" : patientIDError ? "#E54E58" : null,
							}}
						>
							<strong>{validationMessageType}</strong>
							{validationMessageContent}
						</PatientIDValidationMessage>

						<SelectExistingPatientDiv style={{ display: patientIDExists ? "block" : "none" }}>
							<p>
								Would you like to select Patient <strong>{uploadProcessProvider.patientID}</strong> at Site <strong>{uploadProcessProvider.selectedSiteID}</strong>?
							</p>
							<button onClick={() => { handleNavigationToExistingPatient() }}>
								Select
							</button>
						</SelectExistingPatientDiv>
					</div>


				</ModalScrollbar>

				<hr style={{ border: "1px solid var(--color-border-default)", width: "100%", margin: '0px auto' }} />
				<ModalFooter>
					{sitesProvider.userSites?.length !== 1 ? (
						<MobergButton
							theme={MobergTheme.BLUE}
							variant={MobergButtonVariant.OUTLINE}
							shape={MobergButtonShape.WIDE}
							onClick={handleBack}
						>
							Back
						</MobergButton>
					) : (
						<HiddenButton />
					)}
					<MobergButton
						theme={MobergTheme.BLUE}
						variant={MobergButtonVariant.FILLED}
						shape={MobergButtonShape.WIDE}
						onClick={handleNext}
						disabled={!((uploadProcessProvider.existingPatientsCheckedProvider && uploadProcessProvider.patientID) || (uploadProcessProvider.newPatientCheckedProvider && patientIDAvailable))}
					>
						Next
					</MobergButton>
				</ModalFooter>
			</UploadFilesModal>
		</>
	)
}

export default UploadPatientSite
